import { Input, makeStyles, tokens, Spinner } from '@fluentui/react-components';
import { FC, useRef, FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles({
    searchBarWrapper: {    
        marginTop: '0.5rem',
        marginBottom: '0.5rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
    },
    searchInput: {
        minWidth: 0,
        width: '100%',
        borderRadius: '0.5rem',
        background: tokens.colorNeutralBackground1,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        boxShadow: 'none !important',
        fontSize: '1rem',
        '& .fui-Input__input': {
            paddingTop: 0,
            paddingBottom: 0,
            paddingRight: 0,
            paddingLeft: 0,
            boxShadow: 'none !important',
            '::placeholder': {
                color: tokens.colorNeutralForeground3,
            },
        },
        '&:hover': {
            borderBottom: 'none !important',
            border: `1px solid ${tokens.colorNeutralStroke2} !important`,
            background: tokens.colorNeutralBackground1,
            boxShadow: 'none !important',
        },
        '&:focus-within': {
            outline: '2px solid var(--color-primary-400) !important',
            outlineOffset: '2px !important',
            border: `1px solid ${tokens.colorNeutralStroke2} !important`,
            boxShadow: 'none !important',
            background: `${tokens.colorNeutralBackground1} !important`,
        },
        '&::after': {
            display: 'none !important',
            content: 'none !important',
            borderBottom: 'none !important',
        },
        '& .fui-Input__input:focus::after': {
            display: 'none !important',
            content: 'none !important',
            borderBottom: 'none !important',
        },
        '& .fui-Input__input:focus + div::after': {
            display: 'none !important',
        },
        '&:focus-within::after': {
            display: 'none !important',
            content: 'none !important',
            borderBottom: 'none !important',
        },
    },
    lensIcon: {
        background: 'transparent',
        padding: 0,
        marginLeft: '0.75rem',
        marginRight: '0.05rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: tokens.colorNeutralForeground3,
        border: 'none',
        width: '16px',
        height: '16px',
    },
});

export interface SearchInputProps {
    value: string;
    onChange: (_: unknown, data: { value: string }) => void;
    onSearch: () => void;
    onClear: () => void;
    onFocus?: () => void;
    onBlur?: () => void;
    placeholder?: string;
    ariaLabel?: string;
    containerStyle?: React.CSSProperties;
    isSearching?: boolean;
    disabled?: boolean;
}

export const SearchInput: FC<SearchInputProps> = ({
    value,
    onChange,
    onSearch,
    onClear,
    onFocus,
    onBlur,
    placeholder = 'Search …',
    ariaLabel = 'Search',
    containerStyle,
    isSearching = false,
    disabled = false,
}) => {
    const classes = useStyles();
    const { t } = useTranslation();
    const searchContainerRef = useRef<HTMLDivElement>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const handleFormSubmit = (e: FormEvent) => {
        e.preventDefault();
        onSearch();
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            onClear();
        }
    };

    return (
        <div 
            ref={searchContainerRef} 
            className={classes.searchBarWrapper} 
            style={{ position: 'relative', ...containerStyle }}
        >
            <form 
                onSubmit={handleFormSubmit} 
                style={{ 
                    width: '100%', 
                    marginLeft: 'var(--menu-inner-left-margin)', 
                    display: 'flex', 
                    alignItems: 'center' 
                }}
            >
                <Input
                    ref={searchInputRef as unknown as React.RefObject<HTMLInputElement>}
                    placeholder={placeholder}
                    value={value}
                    onChange={onChange}
                    onFocus={onFocus}
                    onBlur={onBlur}
                    onKeyDown={handleKeyDown}
                    disabled={disabled || isSearching}
                    size="large"
                    style={{ paddingLeft: 0, flex: '1 1 auto', minWidth: 0, marginRight: '0.8rem' }}
                    contentBefore={
                        <div 
                            className={classes.lensIcon} 
                            aria-hidden 
                            onClick={!isSearching ? onSearch : undefined} 
                            style={{ cursor: isSearching ? 'default' : 'pointer' }}
                        >
                            {isSearching ? (
                                <Spinner size="tiny" />
                            ) : (
                                <svg width="16" height="16" viewBox="0 0 22 22" fill="none">
                                    <circle cx="10" cy="10" r="7.5" stroke="currentColor" strokeWidth="1.5" fill="none"/>
                                    <rect x="16" y="16" width="7" height="2" rx="1" transform="rotate(45 16 16)" fill="currentColor"/>
                                </svg>
                            )}
                        </div>
                    }
                    contentAfter={
                        <div style={{ width: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                            {value ? (
                                <div
                                    onClick={onClear}
                                    title={t('Clear search')}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', cursor: 'pointer' }}
                                >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                        <path d="M18 6L6 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M6 6L18 18" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                            ) : (
                                <div style={{ width: 18, height: 18, opacity: 0 }} />
                            )}
                        </div>
                    }
                    className={classes.searchInput}
                    autoComplete="off"
                    aria-label={ariaLabel}
                />
            </form>
        </div>
    );
};
